import { useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import {
  libraryCompanyPeopleQueryKeys,
  libraryPeopleQueryKeys,
} from '@/hooks/library/types'
import { usePeopleCount } from '@/hooks/library/usePeopleCount'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { EntityPerson } from '@/types/library/Entity'

interface UsePeopleArgs<T> {
  orgId: UUID | ''
  withConfirmed?: boolean
  withSuggested?: boolean
  select?: (data: PaginatedResponse<EntityPerson>) => T
}

interface UsePeopleOptions<T> extends UsePeopleArgs<T> {
  enabled?: boolean
}

const buildPeopleUrl = ({
  orgId,
  companyId,
  includeRelationships,
  limit,
  offset,
  withConfirmed,
  withSuggested,
}: {
  orgId: UUID
  companyId?: UUID | ''
  includeRelationships?: boolean
  limit?: number
  offset?: number
  withConfirmed?: boolean
  withSuggested?: boolean
}) => {
  const url = new URL(`${API_URL}/api/v1/organizations/${orgId}/entities`)
  url.searchParams.append('entity_types', 'person')
  if (companyId) {
    url.searchParams.append('entity_company_id', String(companyId))
  }
  if (includeRelationships) {
    url.searchParams.append('with_relationships', 'true')
  }
  if (typeof limit === 'number') {
    url.searchParams.append('limit', String(limit))
  }
  if (typeof offset === 'number') {
    url.searchParams.append('offset', String(offset))
  }
  if (typeof withConfirmed === 'boolean') {
    url.searchParams.append('with_confirmed', String(withConfirmed))
  }
  if (typeof withSuggested === 'boolean') {
    url.searchParams.append('with_suggested', String(withSuggested))
  }
  return url
}

export const fetchPeople = async (
  orgId: UUID | '',
  accessToken: string,
  options: {
    companyId?: UUID | ''
    withConfirmed?: boolean
    withSuggested?: boolean
  } = {},
): Promise<PaginatedResponse<EntityPerson>> => {
  if (!orgId) {
    throw new Error('orgId is required')
  }
  const url = buildPeopleUrl({ orgId, ...options })
  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  })
  if (!response.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: response.statusText,
      cause: response,
    })
  }
  return (await response.json()) as PaginatedResponse<EntityPerson>
}

const fetchPerson = async (
  orgId: UUID | '',
  personId: UUID,
  accessToken: string,
  includeRelationships = true,
): Promise<EntityPerson> => {
  if (!orgId) {
    throw new Error('orgId is required')
  }
  const baseUrl = new URL(
    `${API_URL}/api/v1/organizations/${orgId}/entities/${personId}`,
  )
  if (includeRelationships) {
    baseUrl.searchParams.append('with_relationships', 'true')
  }
  const response = await fetch(baseUrl.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  })
  if (!response.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: response.statusText,
      cause: response,
    })
  }
  return (await response.json()) as EntityPerson
}

export const usePeople = <T = EntityPerson[]>({
  orgId,
  withConfirmed,
  withSuggested,
  select,
  enabled = true,
}: UsePeopleOptions<T> & { enabled?: boolean }) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: PaginatedResponse<EntityPerson>) => {
      if (select) {
        return select(data)
      }
      return data.data
        .slice()
        .sort((a, b) =>
          (a.first_name.trim() ?? 'ZZZZ').localeCompare(
            b.first_name.trim() ?? 'ZZZZ',
          ),
        ) as unknown as T
    },
    [select],
  )

  return useQuery({
    queryKey: libraryPeopleQueryKeys.organization({
      orgId,
      withConfirmed,
      withSuggested,
    }),
    queryFn: () =>
      fetchPeople(orgId, accessToken, { withConfirmed, withSuggested }),
    enabled: enabled && !!orgId && !!accessToken,
    staleTime: Infinity,
    placeholderData: (previousData) => previousData,
    select: selectFn,
  })
}

interface UsePersonArgs<T> {
  orgId: UUID | ''
  personId: UUID | ''
  select?: (data: EntityPerson) => T
}

export const usePerson = <T = EntityPerson>({
  orgId,
  personId,
  select,
}: UsePersonArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: EntityPerson) => {
      if (select) {
        return select(data)
      }
      return data as unknown as T
    },
    [select],
  )

  return useQuery({
    queryKey: libraryPeopleQueryKeys.person({
      orgId,
      personId: personId as UUID,
    }),
    queryFn: () => fetchPerson(orgId, personId as UUID, accessToken),
    enabled: !!orgId && !!personId && !!accessToken,
    staleTime: Infinity,
    select: selectFn,
  })
}

interface UseCompanyPeopleArgs<T> {
  orgId: UUID | ''
  companyId: UUID | ''
  select?: (data: PaginatedResponse<EntityPerson>) => T
}

const fetchCompanyPeople = async (
  orgId: UUID | '',
  companyId: UUID | '',
  accessToken: string,
): Promise<PaginatedResponse<EntityPerson>> => {
  if (!orgId || !companyId) {
    throw new Error('orgId and companyId are required')
  }
  return fetchPeople(orgId, accessToken, { companyId })
}

export const useCompanyPeople = <T = EntityPerson[]>({
  orgId,
  companyId,
  select,
}: UseCompanyPeopleArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: PaginatedResponse<EntityPerson>) => {
      if (select) {
        return select(data)
      }
      return data.data as unknown as T
    },
    [select],
  )

  return useQuery({
    queryKey: libraryCompanyPeopleQueryKeys.company({ orgId, companyId }),
    queryFn: () => fetchCompanyPeople(orgId, companyId, accessToken),
    enabled: !!orgId && !!companyId && !!accessToken,
    staleTime: Infinity,
    select: selectFn,
  })
}

interface UseSuggestedPeopleCountArgs {
  orgId: UUID | ''
  enabled?: boolean
}

export const useSuggestedPeopleCount = ({
  orgId,
  enabled = true,
}: UseSuggestedPeopleCountArgs) => {
  return usePeopleCount({
    orgId,
    withConfirmed: false,
    withSuggested: true,
    enabled,
  })
}

/**
 * Selector for person full name
 */
export const selectPersonName = (person: EntityPerson): string => {
  const firstName = person.first_name?.trim() ?? ''
  const lastName = person.last_name?.trim() ?? ''
  const fullName = `${firstName} ${lastName}`.trim()
  return fullName || 'Unnamed Person'
}

interface UsePersonNameArgs {
  orgId: UUID | ''
  personId: UUID | ''
}

/**
 * Hook to fetch just the person name (optimized for page titles)
 * Uses usePeople to leverage already-cached list data
 */
export const usePersonName = ({ orgId, personId }: UsePersonNameArgs) => {
  const selectFn = useCallback(
    (data: PaginatedResponse<EntityPerson>) => {
      const person = data.data.find((p) => p.id === personId)
      return person ? selectPersonName(person) : undefined
    },
    [personId],
  )
  return usePeople({ orgId, select: selectFn })
}
